import { PropsWithChildren, ReactNode } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Chip, styles, Text, Truncated } from '@frontend/design-system';

interface Props {
  children: ReactNode;
}

// TooltipContainer already provides the box chrome (padding/gap/overflow) shared by all tooltip content
export const BaseEventTooltip = ({ children }: Props) => <>{children}</>;

interface TextProps extends Pick<React.ComponentProps<typeof Text>, 'size'> {
  children: string;
  isTruncated?: boolean;
  // primary text is bold and dark; non-primary is regular weight and light colored
  isPrimary?: boolean;
}

BaseEventTooltip.Text = function BaseEventTooltipText({ children, isTruncated, isPrimary, size = 'small' }: TextProps) {
  if (!children) return null;

  if (isTruncated) {
    return (
      <Truncated
        as={Text}
        size={size}
        weight={isPrimary ? 'bold' : 'regular'}
        color={isPrimary ? 'default' : 'subdued'}
      >
        {children}
      </Truncated>
    );
  }

  return (
    <Text
      size={size}
      css={wrappedTextStyle}
      weight={isPrimary ? 'bold' : 'regular'}
      color={isPrimary ? 'default' : 'subdued'}
    >
      {children}
    </Text>
  );
};

BaseEventTooltip.RowList = function RowList({ children }: PropsWithChildren) {
  return <dl css={rowListStyle}>{children}</dl>;
};

interface RowProps {
  label: string;
  value: string;
}

BaseEventTooltip.Row = function Row({ label, value }: RowProps) {
  return (
    <div css={rowStyle}>
      <Text as='dt' size='small' weight='bold' color='light' css={styles.truncate}>
        {label}
      </Text>
      <Chip as='dd' size='tiny' variant='neutral' css={ddChipStyle}>
        {value}
      </Chip>
    </div>
  );
};

const wrappedTextStyle = css({
  display: 'block',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  marginBottom: theme.spacing(0.25),
});

const rowListStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  margin: 0,
  minWidth: 0,
});

const rowStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
});

const ddChipStyle = css({
  margin: 0,
  maxWidth: 'max-content',
});
